/**
 * Stack-member ID helpers.
 *
 * `VariationStack.memberOrder` historically stored raw asset UUIDs but now
 * stores prefixed node IDs (`asset-${uuid}` / `subject-${uuid}`) so a stack
 * can hold both kinds. These helpers centralize the legacy-vs-new
 * normalization so call sites don't reinvent it.
 */

import { parseNodeId, toNodeId, type NodeKind } from '@/lib/engine/nodeIds'

/** Node kinds that may appear inside a variation stack today. */
export type StackMemberKind = Extract<NodeKind, 'asset' | 'subject'>

/** Parsed shape of a `memberOrder` entry. */
export interface StackMember {
  kind: StackMemberKind
  /** Raw domain UUID (no prefix). */
  id: string
}

/**
 * Normalize a single `memberOrder` entry. Accepts:
 *   - `asset-${uuid}` → `{ kind: 'asset', id: uuid }`
 *   - `subject-${uuid}` → `{ kind: 'subject', id: uuid }`
 *   - bare `${uuid}` (legacy stacks pre-mixed-kind support) → `{ kind: 'asset', id }`
 *   - any other prefix (`bin-…`, `job-…`, etc.) → `null`
 *
 * Returns `null` for empty / malformed entries so callers can defensively skip.
 */
export function parseStackMemberId(raw: string): StackMember | null {
  if (!raw) return null
  const parsed = parseNodeId(raw)
  if (parsed) {
    if (parsed.kind === 'asset' || parsed.kind === 'subject') {
      return { kind: parsed.kind, id: parsed.id }
    }
    return null
  }
  // Legacy: a bare UUID with no recognized prefix. Treat as an asset id —
  // before mixed-kind support was added, every member was an asset.
  return { kind: 'asset', id: raw }
}

/**
 * Build a `memberOrder` entry from a parsed member. Always emits the new
 * prefixed form — readers normalize legacy entries on the way in.
 */
export function toStackMemberId(member: StackMember): string {
  return toNodeId(member.kind, member.id)
}
